import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "../../context/toastContext";
import { useConfirm } from "../../context/confirmContext";
import apiClient from "../../api/apiClient";
import Button from "../../components/ui/Button";
import "./InvoiceReview.css";

const WEIGHT_UNITS = ["g", "oz", "lb", "units"];

const InvoiceReview = () => {
    const { invoiceId } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const confirm = useConfirm();

    const [invoice, setInvoice] = useState(null);
    const [lineItems, setLineItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchReviewData();
    }, [invoiceId]);

    const fetchReviewData = async () => {
        try {
            const res = await apiClient.get(`/invoices/${invoiceId}/review`);
            setInvoice(res.data.invoice);
            setLineItems(
                res.data.lineItems.map((item) => ({
                    ...item,
                    resolution: item.match_confidence >= 0.9 ? "merge" : "create",
                    edits: {
                        product_name: item.product_name || "",
                        quantity: item.quantity ?? "",
                        weight_unit: item.weight_unit || "g",
                        cost_per_unit: item.cost_per_unit ?? "",
                        total_cost: item.total_cost ?? "",
                        cultivator_name: item.cultivator_name || "",
                        sku: item.sku || "",
                    },
                }))
            );
        } catch (err) {
            setError(err.response?.data?.error || "Failed to load invoice");
        } finally {
            setLoading(false);
        }
    };

    const updateInvoiceField = (field, value) => {
        setInvoice((prev) => ({ ...prev, [field]: value }));
    };

    const updateLineItem = (index, field, value) => {
        setLineItems((prev) =>
            prev.map((item, i) =>
                i === index ? { ...item, edits: { ...item.edits, [field]: value } } : item
            )
        );
    };

    const setResolution = (index, resolution) => {
        setLineItems((prev) =>
            prev.map((item, i) => (i === index ? { ...item, resolution } : item))
        );
    };

    const removeLineItem = (index) => {
        setLineItems((prev) => prev.map((item, i) =>
            i === index ? { ...item, resolution: "skip" } : item
        ));
    };

    const restoreLineItem = (index) => {
        setLineItems((prev) => prev.map((item, i) =>
            i === index ? { ...item, resolution: item.match_confidence >= 0.9 ? "merge" : "create" } : item
        ));
    };

    const handleConfirm = async () => {
        const activeItems = lineItems.filter((i) => i.resolution !== "skip");
        for (const item of activeItems) {
            if (!item.edits.product_name?.trim()) {
                toast.error("All active items must have a product name.");
                return;
            }
            if (!item.edits.quantity || parseFloat(item.edits.quantity) <= 0) {
                toast.error(`Invalid quantity for "${item.edits.product_name}".`);
                return;
            }
        }

        const createCount = lineItems.filter((i) => i.resolution === "create").length;
        const mergeCount = lineItems.filter((i) => i.resolution === "merge").length;
        const skipCount = lineItems.filter((i) => i.resolution === "skip").length;

        const confirmed = await confirm({
            title: "Confirm Invoice",
            message: `This will create ${createCount} new product(s), merge ${mergeCount} into existing, and skip ${skipCount}. Continue?`,
            confirmLabel: "Confirm & Create",
            cancelLabel: "Cancel",
        });

        if (!confirmed) return;

        setSubmitting(true);
        try {
            const decisions = lineItems.map((item) => ({
                lineItemId: item.id,
                resolution: item.resolution,
                mergeProductId: item.resolution === "merge" ? item.matched_product_id : undefined,
                edits: item.edits,
            }));

            const res = await apiClient.post(`/invoices/${invoiceId}/confirm`, {
                lineItems: decisions,
                invoiceEdits: {
                    invoice_number: invoice.invoice_number,
                    invoice_date: invoice.invoice_date,
                    vendor_name: invoice.vendor_name,
                    total_amount: invoice.total_amount,
                },
            });

            toast.success(
                `Invoice confirmed: ${res.data.created?.length || 0} created, ${res.data.merged?.length || 0} merged.`
            );
            navigate("/Dashboard");
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to confirm invoice");
        } finally {
            setSubmitting(false);
        }
    };

    const confidenceBadge = (confidence) => {
        if (!confidence) return null;
        const pct = Math.round(confidence * 100);
        if (pct >= 90) return <span className="inv-review__match-badge inv-review__match-badge--strong">{pct}% match</span>;
        if (pct >= 60) return <span className="inv-review__match-badge inv-review__match-badge--possible">{pct}% match</span>;
        return null;
    };

    if (loading) {
        return (
            <div className="inv-review">
                <div className="inv-review__loading">Loading invoice data...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="inv-review">
                <div className="inv-review__error">
                    <p>{error}</p>
                    <Button variant="secondary" onClick={() => navigate("/Dashboard")}>Back to Dashboard</Button>
                </div>
            </div>
        );
    }

    const summary = {
        create: lineItems.filter((i) => i.resolution === "create").length,
        merge: lineItems.filter((i) => i.resolution === "merge").length,
        skip: lineItems.filter((i) => i.resolution === "skip").length,
        totalCost: lineItems
            .filter((i) => i.resolution !== "skip")
            .reduce((s, i) => s + (parseFloat(i.edits.total_cost) || 0), 0),
    };

    return (
        <div className="inv-review">
            <div className="inv-review__header">
                <div className="inv-review__header-left">
                    <button className="inv-review__back" onClick={() => navigate("/Dashboard")}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"/>
                        </svg>
                        Dashboard
                    </button>
                    <h2 className="inv-review__title">Review Invoice</h2>
                </div>
            </div>

            {/* Invoice metadata */}
            <div className="inv-review__meta">
                <div className="inv-review__meta-field">
                    <label>Invoice #</label>
                    <input
                        type="text"
                        value={invoice.invoice_number || ""}
                        onChange={(e) => updateInvoiceField("invoice_number", e.target.value)}
                        placeholder="INV-001"
                    />
                </div>
                <div className="inv-review__meta-field">
                    <label>Date</label>
                    <input
                        type="date"
                        value={invoice.invoice_date ? invoice.invoice_date.slice(0, 10) : ""}
                        onChange={(e) => updateInvoiceField("invoice_date", e.target.value)}
                    />
                </div>
                <div className="inv-review__meta-field">
                    <label>Vendor</label>
                    <input
                        type="text"
                        value={invoice.vendor_name || ""}
                        onChange={(e) => updateInvoiceField("vendor_name", e.target.value)}
                        placeholder="Vendor name"
                    />
                </div>
                <div className="inv-review__meta-field">
                    <label>Total</label>
                    <input
                        type="number"
                        step="0.01"
                        value={invoice.total_amount ?? ""}
                        onChange={(e) => updateInvoiceField("total_amount", e.target.value)}
                        placeholder="0.00"
                    />
                </div>
                {invoice.file_url && (
                    <a href={invoice.file_url} target="_blank" rel="noopener noreferrer" className="inv-review__view-file">
                        View Original
                    </a>
                )}
            </div>

            {/* Line items table */}
            <div className="inv-review__table-wrap">
                <table className="inv-review__table">
                    <thead>
                        <tr>
                            <th className="inv-review__th inv-review__th--num">#</th>
                            <th className="inv-review__th inv-review__th--name">Product Name</th>
                            <th className="inv-review__th inv-review__th--qty">Qty</th>
                            <th className="inv-review__th inv-review__th--unit">Unit</th>
                            <th className="inv-review__th inv-review__th--cost">Cost/Unit</th>
                            <th className="inv-review__th inv-review__th--cost">Total</th>
                            <th className="inv-review__th inv-review__th--name">Cultivator</th>
                            <th className="inv-review__th inv-review__th--sku">SKU</th>
                            <th className="inv-review__th inv-review__th--match">Match</th>
                            <th className="inv-review__th inv-review__th--action">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lineItems.map((item, idx) => (
                            <tr key={item.id} className={`inv-review__row${item.resolution === "skip" ? " inv-review__row--skip" : ""}`}>
                                <td className="inv-review__td">{idx + 1}</td>
                                <td className="inv-review__td">
                                    <input
                                        className="inv-review__input"
                                        value={item.edits.product_name}
                                        onChange={(e) => updateLineItem(idx, "product_name", e.target.value)}
                                        disabled={item.resolution === "skip"}
                                    />
                                </td>
                                <td className="inv-review__td">
                                    <input
                                        className="inv-review__input inv-review__input--sm"
                                        type="number"
                                        min="0"
                                        step="any"
                                        value={item.edits.quantity}
                                        onChange={(e) => updateLineItem(idx, "quantity", e.target.value)}
                                        disabled={item.resolution === "skip"}
                                    />
                                </td>
                                <td className="inv-review__td">
                                    <select
                                        className="inv-review__select"
                                        value={item.edits.weight_unit}
                                        onChange={(e) => updateLineItem(idx, "weight_unit", e.target.value)}
                                        disabled={item.resolution === "skip"}
                                    >
                                        {WEIGHT_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                </td>
                                <td className="inv-review__td">
                                    <input
                                        className="inv-review__input inv-review__input--sm"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={item.edits.cost_per_unit}
                                        onChange={(e) => updateLineItem(idx, "cost_per_unit", e.target.value)}
                                        disabled={item.resolution === "skip"}
                                    />
                                </td>
                                <td className="inv-review__td">
                                    <input
                                        className="inv-review__input inv-review__input--sm"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={item.edits.total_cost}
                                        onChange={(e) => updateLineItem(idx, "total_cost", e.target.value)}
                                        disabled={item.resolution === "skip"}
                                    />
                                </td>
                                <td className="inv-review__td">
                                    <input
                                        className="inv-review__input"
                                        value={item.edits.cultivator_name}
                                        onChange={(e) => updateLineItem(idx, "cultivator_name", e.target.value)}
                                        disabled={item.resolution === "skip"}
                                    />
                                </td>
                                <td className="inv-review__td">
                                    <input
                                        className="inv-review__input inv-review__input--sm"
                                        value={item.edits.sku}
                                        onChange={(e) => updateLineItem(idx, "sku", e.target.value)}
                                        disabled={item.resolution === "skip"}
                                    />
                                </td>
                                <td className="inv-review__td">
                                    {item.matched_product_id ? (
                                        <div className="inv-review__match-info">
                                            {confidenceBadge(item.match_confidence)}
                                            <span className="inv-review__match-name" title={item.matched_product_name}>
                                                {item.matched_product_name}
                                            </span>
                                        </div>
                                    ) : (
                                        <span className="inv-review__no-match">No match</span>
                                    )}
                                </td>
                                <td className="inv-review__td">
                                    {item.resolution === "skip" ? (
                                        <button className="inv-review__restore-btn" onClick={() => restoreLineItem(idx)}>
                                            Restore
                                        </button>
                                    ) : (
                                        <div className="inv-review__action-group">
                                            <select
                                                className="inv-review__select"
                                                value={item.resolution}
                                                onChange={(e) => setResolution(idx, e.target.value)}
                                            >
                                                <option value="create">Create New</option>
                                                {item.matched_product_id && (
                                                    <option value="merge">Merge</option>
                                                )}
                                            </select>
                                            <button
                                                className="inv-review__skip-btn"
                                                onClick={() => removeLineItem(idx)}
                                                title="Skip this item"
                                            >
                                                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                                                    <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Summary bar */}
            <div className="inv-review__summary">
                <div className="inv-review__summary-stats">
                    <span className="inv-review__stat">
                        <span className="inv-review__stat-label">Create:</span> {summary.create}
                    </span>
                    <span className="inv-review__stat">
                        <span className="inv-review__stat-label">Merge:</span> {summary.merge}
                    </span>
                    <span className="inv-review__stat inv-review__stat--muted">
                        <span className="inv-review__stat-label">Skip:</span> {summary.skip}
                    </span>
                    <span className="inv-review__stat">
                        <span className="inv-review__stat-label">Total Cost:</span> ${summary.totalCost.toFixed(2)}
                    </span>
                </div>
                <div className="inv-review__summary-actions">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate("/Dashboard")}
                        disabled={submitting}
                    >
                        Discard
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleConfirm}
                        disabled={submitting || lineItems.every((i) => i.resolution === "skip")}
                    >
                        {submitting ? "Confirming..." : "Confirm & Create Products"}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default InvoiceReview;
